import { Component, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ToastController } from '@ionic/angular';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Loading } from 'src/shared/components/loading/loading';
import { Timeout } from 'src/shared/components/timeout/timeout';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';
import { ActivatedRoute, Router } from '@angular/router';
import { Credentials, CredentialModel, CredentialTypeModel, AddCredentialParams } from 'src/providers/credentials/credentials';
import { Aws } from 'src/shared/providers/aws';
import { FilePicker } from 'src/shared/providers/file-picker/file-picker';
import { randomUUID } from 'src/tidy-ui-components/utils/secure-utils';

interface CredentialItem {
  type: CredentialTypeModel;
  existingCredential: CredentialModel | null;
  olderCredentials: CredentialModel[];
  isExpanded: boolean;
  isSaving: boolean;
  formControl: UntypedFormControl;
  toggleValue: boolean;
  uploadedImages: any[];
  uploadedFileUrl: string;
  uploadedFileName: string;
  isUploading: boolean;
  errorMessage: string;
}

interface CredentialSection {
  category: string;
  label: string;
  items: CredentialItem[];
}

const CATEGORY_ORDER = ['core_profile', 'certification', 'external_link'];

const SECTION_LABELS: Record<string, string> = {
  core_profile: 'Business Detail',
  certification: 'Certifications',
  external_link: 'External Links'
};

@Component({
  selector: 'credentials',
  templateUrl: 'credentials.html',
  styleUrls: ['credentials.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class CredentialsPage extends Timeout {
  credentials: CredentialModel[] = [];
  credentialTypes: CredentialTypeModel[] = [];
  sections: CredentialSection[] = [];
  errorMessage: string;

  constructor(
    private credentialsProvider: Credentials,
    private customNavCtrl: CustomNavController,
    private aws: Aws,
    private filePicker: FilePicker,
    private toastCtrl: ToastController,
    timeoutErrorHandler: TimeoutErrorHandler,
    router: Router,
    route: ActivatedRoute
  ) {
    super(timeoutErrorHandler, router, route);
  }

  @Loading()
  async ionViewDidEnter() {
    this.loaded = false;
    try {
      const [credentials, credentialTypes] = await Promise.all([
        this.credentialsProvider.getCredentials(),
        this.credentialsProvider.getCredentialTypes()
      ]);
      this.credentials = credentials;
      this.credentialTypes = credentialTypes;
      this.buildSections();
      this.loaded = true;
    } catch (err) {
      this.timeoutHandler(err);
    }
  }

  buildSections() {
    // A pro can have several of the same type when an edit's cleanup failed; show the newest
    const credentialsByKey: Record<string, CredentialModel[]> = {};
    for (const cred of this.credentials) {
      (credentialsByKey[cred.credential_type.key] ||= []).push(cred);
    }
    for (const creds of Object.values(credentialsByKey)) {
      creds.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    const grouped: Record<string, CredentialItem[]> = {};
    for (const ct of this.credentialTypes) {
      if (!grouped[ct.category]) {
        grouped[ct.category] = [];
      }
      const [existing = null, ...olderCredentials] = credentialsByKey[ct.key] || [];
      const existingFileUrl = existing?.file_url || '';
      const existingImages = (existingFileUrl && ct.key === 'profile_image')
        ? [{ url: existingFileUrl, mediaType: 'photo' }]
        : [];
      const inputType = ct.form_options?.input_type || 'file';
      const initialValue = (inputType === 'textarea' || inputType === 'url' || inputType === 'number')
        ? (existing?.text_value || '')
        : '';
      const fileName = existingFileUrl
        ? (existingFileUrl.split('/').pop() || 'File uploaded')
        : '';
      grouped[ct.category].push({
        type: ct,
        existingCredential: existing,
        olderCredentials,
        isExpanded: false,
        isSaving: false,
        formControl: new UntypedFormControl(initialValue),
        toggleValue: !!existing,
        uploadedImages: existingImages,
        uploadedFileUrl: existingFileUrl,
        uploadedFileName: fileName,
        isUploading: false,
        errorMessage: ''
      });
    }

    this.sections = CATEGORY_ORDER
      .filter(cat => grouped[cat]?.length > 0)
      .map(cat => ({
        category: cat,
        label: SECTION_LABELS[cat] || cat,
        items: grouped[cat]
      }));
  }

  toggleExpand(item: CredentialItem) {
    item.isExpanded = !item.isExpanded;
  }

  getInputType(item: CredentialItem): string {
    return item.type.form_options?.input_type || 'file';
  }

  hasFileUrl(item: CredentialItem): boolean {
    return !!item.existingCredential?.file_url;
  }

  isImageUrl(url: string): boolean {
    if (!url) return false;
    const lower = url.toLowerCase().split('?')[0];
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(lower)) return true;
    if (/\.(pdf|doc|docx|xls|xlsx|csv|txt|zip)$/.test(lower)) return false;
    // No recognizable extension — assume image (e.g. picsum.photos, S3 keys without extensions)
    return true;
  }

  getFileTypeLabel(url: string): string {
    if (!url) return 'File';
    const lower = url.toLowerCase().split('?')[0];
    const ext = lower.split('.').pop() || '';
    if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'zip'].includes(ext)) {
      return ext.toUpperCase();
    }
    return 'File';
  }

  getDisplayValue(item: CredentialItem): string {
    if (!item.existingCredential) {
      return 'None';
    }
    const inputType = this.getInputType(item);
    if (inputType === 'textarea' || inputType === 'url' || inputType === 'number') {
      return item.existingCredential.text_value || 'None';
    }
    if (inputType === 'toggle') {
      return 'Yes';
    }
    if (item.existingCredential.file_url) {
      return 'File uploaded';
    }
    return 'None';
  }

  shouldShowStatusIcon(item: CredentialItem): boolean {
    if (!item.existingCredential) return false;
    return item.existingCredential.status !== 'confirmed';
  }

  getStatusChipText(item: CredentialItem): string {
    if (!item.existingCredential) return '';
    if (item.existingCredential.status === 'rejected') {
      return 'Rejected' + (item.existingCredential.rejection_reason ? ' – ' + item.existingCredential.rejection_reason : '');
    }
    return 'Pending concierge review';
  }

  hasActiveConfirmedCredential(item: CredentialItem): boolean {
    return item.existingCredential?.status !== 'confirmed'
      && item.olderCredentials.some(c => c.status === 'confirmed');
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'confirmed': return 'Confirmed';
      case 'rejected': return 'Rejected';
      default: return 'Pending Review';
    }
  }

  removeFile(item: CredentialItem) {
    item.uploadedFileUrl = '';
    item.uploadedFileName = '';
    item.uploadedImages = [];
  }

  async pickFile(item: CredentialItem) {
    item.errorMessage = '';
    item.isUploading = true;
    try {
      const file = await this.filePicker.pickFile();
      const fileName = file.filename.replace(/ /g, '');
      const s3Key = `homekeeper-credentials/${randomUUID()}/${fileName}`;
      const response = await this.aws.uploadFileToS3(file.dataUrl, s3Key, file.format);
      item.uploadedFileUrl = response.Location;
      item.uploadedFileName = fileName;
    } catch (err) {
      if (err?.message?.includes('selection was cancelled')) return;
      item.errorMessage = 'Failed to upload file. Please try again.';
      item.uploadedFileUrl = '';
      item.uploadedFileName = '';
      this.showUploadErrorToast();
    } finally {
      item.isUploading = false;
    }
  }

  async saveCredential(item: CredentialItem) {
    item.errorMessage = '';
    item.isSaving = true;

    try {
      const inputType = this.getInputType(item);
      const formValue = item.formControl.value;

      if (inputType === 'url' && !formValue?.trim()) {
        item.errorMessage = 'Please enter a valid URL.';
        return;
      }
      if (inputType === 'textarea' && !formValue?.trim()) {
        item.errorMessage = 'Please enter a value.';
        return;
      }
      if (inputType === 'number') {
        const num = Number(formValue);
        const min = item.type.form_options?.min;
        const max = item.type.form_options?.max;
        if (formValue === '' || isNaN(num) || (min != null && num < min) || (max != null && num > max)) {
          item.errorMessage = `Please enter a number between ${min} and ${max}.`;
          return;
        }
      }
      if (inputType === 'file' && !item.uploadedFileUrl) {
        item.errorMessage = 'Please upload a file.';
        return;
      }

      // Toggle OFF: just delete existing
      if (inputType === 'toggle' && !item.toggleValue) {
        await this.removeAllCredentials(item);
        item.isExpanded = false;
        return;
      }

      // File no-op: no new upload
      if (inputType === 'file' && item.existingCredential && item.uploadedFileUrl === item.existingCredential.file_url) {
        item.isExpanded = false;
        return;
      }

      const data: AddCredentialParams = {
        credential_type_key: item.type.key
      };
      if (inputType === 'url' || inputType === 'textarea') {
        data.text_value = formValue.trim();
      }
      if (inputType === 'number') {
        data.text_value = String(formValue);
      }
      if (inputType === 'toggle') {
        data.text_value = 'true';
      }
      if (inputType === 'file') {
        data.file_url = item.uploadedFileUrl;
      }

      // Unchanged confirmed value: nothing new to review
      if (inputType !== 'file' && item.existingCredential?.status === 'confirmed' && data.text_value === item.existingCredential.text_value) {
        item.isExpanded = false;
        return;
      }

      // Create before deleting the old one so a failed save keeps what the pro already had
      const newCredential = await this.credentialsProvider.addCredential(data);
      const replacedCredentials = [item.existingCredential, ...item.olderCredentials].filter(Boolean);
      // A confirmed one keeps counting until the edit is confirmed; the backend drops it then
      const confirmedCredentials = replacedCredentials.filter(c => c.status === 'confirmed');
      const otherCredentials = replacedCredentials.filter(c => c.status !== 'confirmed');
      item.existingCredential = newCredential;
      item.isExpanded = false;
      item.olderCredentials = [...confirmedCredentials, ...await this.removeReplacedCredentials(otherCredentials)];
    } catch (err) {
      item.errorMessage = err.message || err.error?.message || 'An error occurred.';
    } finally {
      item.isSaving = false;
    }
  }

  // The new credential is already saved, so a failed cleanup isn't an error; what's left stays on the item for Delete, toggle-off and the next save
  async removeReplacedCredentials(credentials: CredentialModel[]): Promise<CredentialModel[]> {
    const leftovers: CredentialModel[] = [];
    for (const credential of credentials) {
      try {
        await this.deleteCredentialUnlessGone(credential.id);
      } catch (err) {
        console.error(err);
        leftovers.push(credential);
      }
    }
    return leftovers;
  }

  // Removes the shown credential and any older ones of its type so none come back on reload
  async removeAllCredentials(item: CredentialItem) {
    const remaining = [item.existingCredential, ...item.olderCredentials].filter(Boolean);
    try {
      while (remaining.length) {
        await this.deleteCredentialUnlessGone(remaining[0].id);
        remaining.shift();
      }
    } finally {
      item.existingCredential = remaining[0] || null;
      item.olderCredentials = remaining.slice(1);
    }
  }

  async deleteCredentialUnlessGone(id: number) {
    try {
      await this.credentialsProvider.deleteCredential(id);
    } catch (err) {
      // The server may have dropped it already: confirming a newer credential soft-deletes the previous one
      if (err?.code !== 404) throw err;
    }
  }

  async deleteCredential(item: CredentialItem) {
    item.errorMessage = '';
    try {
      if (item.existingCredential) {
        await this.removeAllCredentials(item);
        item.formControl.setValue('');
        item.toggleValue = false;
        item.uploadedFileUrl = '';
        item.uploadedFileName = '';
        item.uploadedImages = [];
      }
    } catch (err) {
      item.errorMessage = err.message || err.error?.message || 'Failed to delete.';
    }
  }

  async onFilesAdded(item: CredentialItem, images: any[]) {
    item.errorMessage = '';
    item.isUploading = true;
    try {
      const image = images[images.length - 1];
      const s3Key = `homekeeper-credentials/${randomUUID()}`;
      const response = await this.aws.uploadImageToS3(image.url, s3Key, image.file, image.exif);
      item.uploadedFileUrl = response.Location;
      item.uploadedImages = images;
    } catch (err) {
      item.errorMessage = 'Failed to upload file. Please try again.';
      item.uploadedImages = [];
      item.uploadedFileUrl = '';
      this.showUploadErrorToast();
    } finally {
      item.isUploading = false;
    }
  }

  onFileRemoved(item: CredentialItem) {
    item.uploadedImages = [];
    item.uploadedFileUrl = '';
  }

  onFileError(item: CredentialItem, error: any) {
    item.errorMessage = error?.message || 'Invalid file. Please try a different file.';
  }

  onToggleChange(item: CredentialItem, value: boolean) {
    item.toggleValue = value;
  }

  async showUploadErrorToast() {
    const toast = await this.toastCtrl.create({
      message: 'Failed to upload file. Please try again.',
      duration: 5000,
      cssClass: 'toast-info',
      icon: 'alert-circle-outline',
    });
    await toast.present();
  }
}
